import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export type NoticeTone = 'info' | 'warn' | 'error' | 'success';

const TONES: Record<NoticeTone, string> = {
  info: 'bg-primary-soft text-ink',
  warn: 'bg-attention-soft text-attention-ink',
  error: 'bg-danger-soft text-danger',
  success: 'bg-success-soft text-success',
};

/**
 * An inline message that screen readers announce: `role="alert"` for an error, `role="status"` for the
 * rest. Shared by every surface (it replaced four local copies). `action` sits at the end of the row and
 * wraps under the text on a narrow screen.
 */
export function Notice({
  tone = 'info',
  children,
  action,
  className,
}: {
  tone?: NoticeTone;
  children: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn(
        'flex flex-wrap items-start justify-between gap-3 rounded-control px-3 py-2.5 text-sm font-medium',
        TONES[tone],
        className,
      )}
    >
      <div className="min-w-0 break-words">{children}</div>
      {action}
    </div>
  );
}
